/**
 * Operator preferences that belong to this browser, not to the account.
 *
 * ## 🔴 Why `localStorage` and not a setting on the server
 *
 * `SettingsScreen`'s own header records the rule this does not break: *"Nothing configurable has
 * moved out of YAML"* — because a live change to what the **services** read means deciding what
 * that change is allowed to do mid-trade. That argument is about machine configuration.
 *
 * Which view a screen opens on is a different category. It changes nothing the engine reads, it
 * cannot be wrong in a way that costs money, and it is genuinely per-device: the morning read
 * happens on a desktop and the interruption often on a phone, and those two want different
 * defaults. Persisting it server-side would need a schema, a migration and an endpoint to express
 * something no other person will ever read.
 *
 * ⚠ **So the boundary is: nothing here may ever affect what is traded.** A preference that changed
 * a threshold, a size or a limit would belong in YAML under H9's rule that the UI may never be the
 * thing that prevents a breach. These are view defaults and must stay view defaults.
 *
 * ## Reading is total and never throws
 *
 * `localStorage` throws in a private window, when a quota is exhausted, and when the browser is
 * configured to refuse it. A preference read that can throw is a preference read that can take a
 * screen down for a stored default, so every path here falls back to the documented default and
 * an unrecognised stored value is treated as absent rather than trusted.
 *
 * ⚠ **Reached through `globalThis`, not `window`, and that is not a test convenience.** Naming
 * `window` in a module that promises never to throw is itself a throw wherever `window` does not
 * exist — a bare `ReferenceError`, before any `try` can catch it, from the one function whose
 * contract is that it always answers. `storage()` returns `null` instead, which is the same
 * "storage unavailable" case a private window produces and is already handled.
 */

/** The Fleet screen's three views. Mirrors `FleetScreen`'s `VIEWS`. */
export type FleetView = 'families' | 'rows' | 'executions';

/**
 * 🔴 **`families` is the product default and stays it.** `FleetScreen`'s own docstring:
 * *"Seven rows is the morning read"*, and §8's reading is by neighbourhood rather than by row.
 * This lets the operator choose otherwise; it does not change what the product opens on for
 * someone who has never chosen.
 */
export const DEFAULT_FLEET_VIEW: FleetView = 'families';

const FLEET_VIEW_KEY = 'jmt.fleet.view';

function isFleetView(value: unknown): value is FleetView {
  return value === 'families' || value === 'rows' || value === 'executions';
}

/** The two themes. Mirrors `AppShell`'s `Theme`, which owns applying it to the root element. */
export type Theme = 'dark' | 'light';

/**
 * 🔴 **Dark is the product default and stays it.** `Visual-Design-Language.md` is a dark-first
 * document — the token set, the annunciator register and the mode edge are all designed against a
 * dark canvas, and light is the accommodation rather than the other way round.
 *
 * ⚠ Deliberately **not** `prefers-color-scheme`. This is a single-operator console whose canvas is
 * part of its design, not a document that should follow the OS; and an operator who chose light
 * would have it silently undone by a system setting they changed for something else.
 */
export const DEFAULT_THEME: Theme = 'dark';

const THEME_KEY = 'jmt.theme';

function isTheme(value: unknown): value is Theme {
  return value === 'dark' || value === 'light';
}

/** The stored theme, or the default. Never throws — see the module note. */
export function readTheme(): Theme {
  try {
    const stored = storage()?.getItem(THEME_KEY);
    return isTheme(stored) ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/** Persist the theme. Silently a no-op when storage is unavailable — see the module note. */
export function writeTheme(theme: Theme): void {
  try {
    storage()?.setItem(THEME_KEY, theme);
  } catch {
    // See `writeFleetView`. The theme still applies for this session.
  }
}

/** How timestamps are rendered. Mirrors `ui/timestamps.ts`, which owns the formatting. */
export type TimestampMode = 'utc' | 'local';

/**
 * 🔴 **`utc` is the spec's default and stays it.** `UI-Design-Spec.md` line 120: *"UTC by default
 * with a toggle for local time … Mixed timezones is how people misread a trade log."*
 *
 * ⚠ Deliberately **not** defaulted to the browser's zone, which is the obvious convenience and the
 * wrong call: it would mean two operators — or one operator on two devices in different zones —
 * reading the same trade log as different times, with nothing on screen having changed. The mode is
 * a choice somebody makes, and until they make it the answer is the zone the system records in.
 */
export const DEFAULT_TIMESTAMP_MODE: TimestampMode = 'utc';

const TIMESTAMP_MODE_KEY = 'jmt.timestamps.mode';

function isTimestampMode(value: unknown): value is TimestampMode {
  return value === 'utc' || value === 'local';
}

/** The browser's storage, or `null` where there is none. Never throws — see the module note. */
function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Accessing the property itself throws when storage is disabled by policy, which is a
    // different failure from `getItem` throwing and is why the guard is here rather than inside.
    return null;
  }
}

/** The stored Fleet view, or the default. Never throws, and never returns a value it cannot name. */
export function readFleetView(): FleetView {
  try {
    const stored = storage()?.getItem(FLEET_VIEW_KEY);
    return isFleetView(stored) ? stored : DEFAULT_FLEET_VIEW;
  } catch {
    // A private window, a full quota, or storage refused outright. The default is a correct
    // answer to "what should this screen open on", so there is nothing to report.
    return DEFAULT_FLEET_VIEW;
  }
}

/** Persist the Fleet view. Silently a no-op when storage is unavailable — see the module note. */
export function writeFleetView(view: FleetView): void {
  try {
    storage()?.setItem(FLEET_VIEW_KEY, view);
  } catch {
    // The screen still switches for this session; only the memory of it is lost, and a toast
    // about browser storage is not something the operator can act on.
  }
}

/** The stored timestamp mode, or the default. Never throws — see the module note. */
export function readTimestampMode(): TimestampMode {
  try {
    const stored = storage()?.getItem(TIMESTAMP_MODE_KEY);
    return isTimestampMode(stored) ? stored : DEFAULT_TIMESTAMP_MODE;
  } catch {
    return DEFAULT_TIMESTAMP_MODE;
  }
}

/**
 * Persist the timestamp mode. Silently a no-op when storage is unavailable.
 *
 * ⚠ The mode still applies for this session when the write fails, which is the right half to keep:
 * the operator asked to read local time and a storage failure is not a reason to refuse.
 */
export function writeTimestampMode(mode: TimestampMode): void {
  try {
    storage()?.setItem(TIMESTAMP_MODE_KEY, mode);
  } catch {
    // See `writeFleetView`.
  }
}
